import Separator from "@/common/components/separator";
import useHttpProvider from "@/common/hooks/use-http";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Purchase } from "@/features/payments/models/purchase";
import * as PurchasesServices from "@/features/payments/services/purchases";
import { formatPrice } from "@/lib/string";
import { useEffect, useState } from "react";

function ProfilePurchaseHistoryPage() {
	const { http } = useHttpProvider();
	const [purchases, setPurchases] = useState<Purchase[]>([]);

	const getUserPurchaseHistory = async () => {
		const response = await PurchasesServices.getUserPurchaseHistory(http);
		setPurchases(response);
	};

	useEffect(() => {
		getUserPurchaseHistory();
	}, []);

	const getStatusBadge = (purchase: Purchase) => {
		if (purchase.purchaseType !== "merchandise") return <span>-</span>;

		const trackingLine = purchase.merchTrackingNumber ? (
			<span className="web-body-b3 text-gray-700">
				{purchase.merchShippingCarrier
					? `${purchase.merchShippingCarrier} · `
					: ""}
				{purchase.merchTrackingUrl ? (
					<a
						href={purchase.merchTrackingUrl}
						target="_blank"
						rel="noreferrer noopener"
						className="text-blue-700 underline underline-offset-2"
					>
						{purchase.merchTrackingNumber}
					</a>
				) : (
					purchase.merchTrackingNumber
				)}
			</span>
		) : null;

		const statusEl =
			purchase.merchOrderStatus === "delivering" ? (
				<span className="web-body-b2 text-blue-700">Delivering</span>
			) : purchase.merchOrderStatus === "delivered" ? (
				<span className="web-body-b2 text-green-700">Delivered</span>
			) : (
				<span className="web-body-b2 text-gray-500">Pending Delivery</span>
			);

		if (!trackingLine) return statusEl;
		return (
			<span className="flex flex-col gap-1">
				{statusEl}
				{trackingLine}
			</span>
		);
	};

	return (
		<div className="flex flex-col gap-8">
			<h1 className="web-heading-h3-medium">Purchase History</h1>

			<Table>
				<TableHeader className="rounded-[32px] border-0 bg-[rgba(46,_72,_219,_0.05)]">
					<TableRow className="border-0!">
						<TableHead className="web-body-b1 rounded-l-[32px] p-4 text-gray-500">
							ID
						</TableHead>
						<TableHead className="web-body-b1 p-4 text-gray-500">
							Package Name
						</TableHead>
						<TableHead className="web-body-b1 p-4 text-gray-500">
							Draw
						</TableHead>
						<TableHead className="web-body-b1 p-4 text-gray-500">
							Price
						</TableHead>
						<TableHead className="web-body-b1 p-4 text-gray-500">
							Status
						</TableHead>
						<TableHead className="web-body-b1 rounded-r-[32px] p-4 text-gray-500">
							Billing Date
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{purchases.map((purchase, index) => (
						<>
							<TableRow key={purchase.id} className="border-0">
								<TableCell className="web-body-b1 rounded-l-[32px] px-4 text-left">
									{purchase.id}
								</TableCell>
								<TableCell className="web-body-b1 px-4 text-left">
									{purchase.name
										? purchase.name
										: purchase.entryCount
										? `Entry Purchase - (${purchase.entryCount}) ${purchase.entryCount === 1 ? "entry" : "entries"}`
										: `Top Up - (${purchase.amount}) ${purchase.amount === 1 ? "entry" : "entries"}`}
								</TableCell>
								<TableCell className="web-body-b1 px-4 text-left">
									{purchase.drawName || "-"}
								</TableCell>
								<TableCell className="web-body-b1 px-4 text-left">
									{formatPrice(purchase.amount, purchase.currency)}
								</TableCell>
								<TableCell className="web-body-b1 px-4 text-left">
									{getStatusBadge(purchase)}
								</TableCell>
								<TableCell className="gap-2 rounded-r-[32px] px-4 py-6">
									<p className="web-body-b1">
										{new Intl.DateTimeFormat("en-GB").format(
											new Date(purchase.createdAt)
										)}
									</p>
								</TableCell>
							</TableRow>
							{index !== purchases.length - 1 && (
								<tr>
									<td colSpan={6} className="h-1">
										<Separator />
									</td>
								</tr>
							)}
						</>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

export default ProfilePurchaseHistoryPage;
